import React from 'react';
import { Check, Info } from 'lucide-react';
import { Button } from './ui/button';
import { servicesData, trainingPhotos, massageData, seminarGallery, seminarIntro, pricingData } from '../utils/mockData';

const Services = ({ onContactClick }) => {
  return (
    <section id="services" className="section-padding">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="section-title">My Services</h2>
          <div className="section-title-underline"></div>
          <p className="section-description">
            Comprehensive fitness and wellness solutions tailored to your goals
          </p>
        </div>

        <div className="services-grid">
          {servicesData.map((service, index) => (
            <div key={service.id} className="service-card">
              <div className="service-image-wrapper">
                <img 
                  src={service.image} 
                  alt={service.altText || service.title}
                  className="service-image"
                />
                <div className="service-image-overlay"></div>
              </div>
              <div className="service-content">
                <h3 className="service-title">{service.title}</h3>
                <p className="service-description">{service.description}</p>
                <ul className="service-features">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="service-feature-item">
                      <Check className="feature-check-icon" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button 
                  onClick={onContactClick}
                  className="service-cta"
                >
                  Enquire Now
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Personal Training Gallery */}
        <div className="service-detail-section">
          <h3 className="service-detail-title">Personal Training in Action</h3>
          <p className="service-detail-description">
            With 9 years of experience and 100s of clients transformed, I'm dedicated to helping you achieve the results you deserve.
          </p>
          <div className="photo-gallery">
            {trainingPhotos.map((photo, index) => (
              <div key={index} className="gallery-item">
                <img src={photo} alt={`Personal training session ${index + 1}`} className="gallery-image" />
              </div>
            ))}
          </div>

          {/* Introductory Offer */}
          <div className="pricing-note-card" data-testid="pt-intro-offer" style={{borderColor: '#b1d44c', marginTop: '2rem'}}>
            <Info className="pricing-note-icon" />
            <div>
              <h4 className="pricing-note-title">Introductory Offer</h4>
              <p className="pricing-note-text">
                Try <strong>2 initial 1-hour sessions for just £50</strong> before signing up to a package. No commitment — just a chance to see if we're the right fit.
              </p>
              <Button onClick={onContactClick} className="pricing-note-cta">
                Claim Intro Offer
              </Button>
            </div>
          </div>

          {/* Pricing Note */}
          <div className="pricing-note-card" data-testid="pt-pricing-note">
            <Info className="pricing-note-icon" />
            <div>
              <h4 className="pricing-note-title">Personal Training Pricing</h4>
              <p className="pricing-note-text">
                Different tiers of support are available to suit your goals and budget, starting from as little as <strong>£100 a month</strong>. Get in touch to discuss which option is right for you.
              </p>
              <Button onClick={onContactClick} className="pricing-note-cta">
                Get Pricing Information
              </Button>
            </div>
          </div>
        </div>

        {/* Massage Therapy Section */}
        <div className="service-detail-section">
          <h3 className="service-detail-title">{massageData.title}</h3>
          <p className="service-detail-description">
            {massageData.description}
          </p>
          
          <div className="single-training-image">
            <img 
              src={massageData.images[0].url} 
              alt={massageData.images[0].alt} 
              className="training-hero-image" 
            />
          </div>

          {/* Cupping Therapy */}
          <div className="cupping-section">
            <h4 className="cupping-title">Cupping Therapy</h4>
            <p className="cupping-description">{massageData.cuppingDescription}</p>
            <div className="cupping-video-wrapper">
              <img 
                src={massageData.cuppingImage.url} 
                alt={massageData.cuppingImage.alt}
                className="cupping-video-placeholder"
              />
            </div>
          </div>

          {/* Massage Pricing */}
          <div className="massage-pricing-grid">
            {massageData.pricing.map((item, index) => (
              <div key={index} className="pricing-card">
                <h4 className="pricing-card-service">{item.service}</h4>
                <p className="pricing-card-duration">{item.duration}</p>
                <p className="pricing-card-price">{item.price}</p>
                {item.note && (
                  <p className="pricing-card-note">{item.note}</p>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Seminars Section */}
        <div className="service-detail-section">
          <h3 className="service-detail-title">{seminarIntro.title}</h3>
          <p className="service-detail-description">
            {seminarIntro.description}
          </p>
          
          <div className="seminars-timeline">
            {seminarGallery.map((seminar) => (
              <div key={seminar.id} className="seminar-timeline-card">
                <div className="seminar-timeline-image">
                  <img src={seminar.url} alt={seminar.title} className="seminar-image" />
                  <div className="seminar-date-badge">{seminar.date}</div>
                </div>
                <div className="seminar-timeline-content">
                  <h4 className="seminar-timeline-title">{seminar.title}</h4>
                  <p className="seminar-speaker">with Special Guest Speaker: {seminar.speaker}</p>
                  <p className="seminar-timeline-description">{seminar.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Wellness Retreats Section */}
        <div className="service-detail-section">
          <h3 className="service-detail-title">Wellness Retreats</h3>
          <p className="service-detail-description">
            Transformative wellness retreats that combine intensive fitness training, nutrition education, 
            mindfulness practices, and recovery sessions in inspiring locations. These immersive experiences 
            are designed to kickstart your transformation, break through plateaus, and provide you with 
            sustainable lifestyle tools. Limited spaces available - perfect for those ready to commit to 
            meaningful change in a supportive group environment.
          </p>
          <div className="retreat-highlight">
            <div className="retreat-content">
              <h4 className="retreat-subtitle">What to Expect</h4>
              <ul className="retreat-features">
                <li>Daily training sessions with personalized attention</li>
                <li>Nutrition workshops and meal planning guidance</li>
                <li>Mindfulness and recovery practices</li>
                <li>Group activities and team building</li>
                <li>Expert guest speakers and educational seminars</li>
                <li>Ongoing support post-retreat</li>
              </ul>
              <Button 
                onClick={onContactClick}
                className="retreat-cta"
              >
                Enquire About Retreats
              </Button>
            </div>
            <div className="retreat-image-wrapper">
              <img 
                src="https://customer-assets.emergentagent.com/job_e62ce57c-3c16-45b9-ad98-384d5c2e55e7/artifacts/y51hzrbf_7f23e3e6-ead5-43f4-a17a-5becd5d52a84.avif"
                alt="Wellness retreat fitness session"
                className="retreat-image"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
